import {
  Box,
  Typography,
  Modal,
} from '@mui/material';
import {
  Dashboard,
  CloudDownload,
  NotificationsActive,
  Star,
  Close,
  Logout,
} from '@mui/icons-material';
import BrandLogo from './auth/BrandLogo';
import { useAuth } from '../contexts/AuthContext';
import { avatarGradient, fontStack } from './auth/authStyles';

const C = {
  bg: '#1a1b2e',
  card: '#2E2F45',
  input: '#25263B',
  border: '#3A3B55',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  danger: '#FF5722',
  accent: '#34D399',
};

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Tableau de bord', Icon: Dashboard },
  { id: 'download', label: 'Télécharger', Icon: CloudDownload },
  { id: 'alerts', label: 'Alertes', Icon: NotificationsActive, badge: true },
  { id: 'favorites', label: 'Favoris', Icon: Star },
];

const MobileNavMenu = ({
  open,
  onClose,
  currentMenu,
  onMenuClick,
  alertCount = 0,
}) => {
  const { user, logout } = useAuth();

  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';

  const handleNav = (id) => {
    onMenuClick(id);
    onClose();
  };

  const isActive = (id) => {
    if (id === 'favorites') return currentMenu === 'dashboard';
    return currentMenu === id;
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1400 }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: C.bg,
          fontFamily: fontStack,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '16px' }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            aria-label="Fermer le menu"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              cursor: 'pointer',
              backgroundColor: 'rgba(255,255,255,0.07)',
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.textPrimary,
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            pb: '28px',
          }}
        >
          <BrandLogo iconSize={64} fontSize="18px" textColor={C.textPrimary} vertical />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', px: '16px' }}>
          {NAV_ITEMS.map(({ id, label, Icon, badge }) => {
            const active = isActive(id);
            return (
              <Box
                key={id}
                component="button"
                type="button"
                onClick={() => handleNav(id)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  p: '14px',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: active ? C.textPrimary : C.textMuted,
                  textAlign: 'left',
                }}
              >
                <Icon sx={{ fontSize: 20, color: active ? C.accent : C.textMuted }} />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '15px',
                    fontWeight: active ? 700 : 500,
                    color: 'inherit',
                  }}
                >
                  {label}
                </Typography>
                {badge && alertCount > 0 && (
                  <Box
                    sx={{
                      borderRadius: '999px',
                      px: '9px',
                      py: '2px',
                      backgroundColor: C.danger,
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>
                      {alertCount > 99 ? '99+' : alertCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            p: '16px',
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: avatarGradient,
              color: '#0B1320',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            {initials}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: C.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {firstName} {lastName}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: C.textPlaceholder,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={logout}
            aria-label="Déconnexion"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              cursor: 'pointer',
              backgroundColor: 'rgba(255,255,255,0.07)',
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.textMuted,
            }}
          >
            <Logout sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MobileNavMenu;
